/**
 * Widget resource service — Dashboard API, widget module (CT-5183).
 *
 * Eight real endpoints under `/v0/dashboard/widget`, no transport seam: the
 * mock router this file used to go through is gone. The vocabulary on the wire
 * is the backend's, **camelCase** (`widgetUuid`, `typeCode`, `categoryKey`,
 * `isGoverned`), not the `snake_case` the rest of the platform uses — that is
 * what this gateway returns, verified against the live stage, so it is what the
 * mappers speak.
 *
 * Everything the UI renders that the contract has no field for degrades to
 * empty here rather than being invented: `integration`, `profile`, `freshness`,
 * `usedOnDashboards`, `dashboards` and `lastUpdated` are not part of the
 * WidgetDefinition, so the row and the detail panel simply do not show them.
 */

import { validateApiResponse, normalizePagedMeta } from '../shared/utils/apiResponse.js';
import { apiDashboards, WIDGET_API_PREFIX } from './apiClient.js';

/** Lifecycle the backend models: a widget is created as a draft and published. */
export const WIDGET_STATUSES = Object.freeze(['DRAFT', 'PUBLISHED']);

/** `provenance` enum, rejected by the API when it is anything else. */
export const WIDGET_PROVENANCES = Object.freeze([
  'EXISTING_DATASET',
  'ENTITY_BUILDER',
  'SYSTEM_PROVIDER',
]);

/** `dataSource.mode` enum. Phase 2 of the backend only implements `ENTITY`. */
export const WIDGET_DATA_SOURCE_MODES = Object.freeze(['ENTITY', 'EXISTING_QUERY']);

/** Which stored version `getWidget` reads. */
export const WIDGET_VERSIONS = Object.freeze(['PUBLISHED', 'DRAFT']);

/**
 * Sort fields the library offers. Only `name` is here: `orderBy` is a free
 * string on the wire (nothing is rejected), so an id the backend does not sort
 * on would look like a working control that silently changes nothing.
 */
export const WIDGET_ORDER_BY = Object.freeze(['name']);

const DEFAULT_PAGE_SIZE = 24;

/**
 * `dataSource.entity.resultMode` ⇄ the builder's operation.
 *
 * The type catalog gates on the backend vocabulary (`allowedDataModes`), and a
 * type that does not accept a mode rejects the save outright — `Widget type
 * TABLE does not support SUMMARY`.
 */
export const RESULT_MODE_BY_OPERATION = Object.freeze({
  summarize: 'SUMMARY',
  record_set: 'RECORD_SET',
});

const RESULT_MODE_TO_OPERATION = Object.freeze({
  SUMMARY: 'summarize',
  RECORD_SET: 'record_set',
});

/**
 * The builder's operator ⇄ the one `mdl_query_filters` stores.
 *
 * The two vocabularies are not the same, and sending ours straight through was
 * a silent loss: `is_empty` and `is_not_empty` came back
 * `Unsupported filter operator` at **create**, reported to the author as a
 * plain "could not save". The backend's canonical set is `FILTER_OPERATORS`
 * in `widget.py`; it accepts several spellings and normalises to these.
 *
 * `between` is deliberately absent — the backend has no such operator in any
 * spelling, so the payload expands that row into a `GTE` and an `LTE`. See
 * `buildWidgetPayload`.
 */
export const FILTER_OPERATOR_TO_WIRE = Object.freeze({
  equals: 'EQ',
  not_equals: 'NEQ',
  contains: 'CONTAINS',
  starts_with: 'STARTS_WITH',
  greater_than: 'GT',
  greater_or_equal: 'GTE',
  less_than: 'LT',
  less_or_equal: 'LTE',
  // Emptiness is null-ness on the wire; these two names are the whole reason
  // this map exists.
  is_empty: 'IS_NULL',
  is_not_empty: 'IS_NOT_NULL',
});

/**
 * The way back, for a widget reopened to edit.
 *
 * Without it the stored `EQ` reached a select that only knows `equals`, and
 * the row came back with no operator at all — a filter that looked unfinished
 * to the author and lost its column's meaning on the next save.
 */
export const FILTER_OPERATOR_FROM_WIRE = Object.freeze(
  Object.fromEntries(Object.entries(FILTER_OPERATOR_TO_WIRE).map(([ui, wire]) => [wire, ui])),
);

/** Drop `undefined` / `''` / `[]` so an untouched filter never reaches the wire. */
const compact = (body) =>
  Object.fromEntries(
    Object.entries(body).filter(([, value]) => {
      if (value === undefined || value === null || value === '') return false;
      if (Array.isArray(value)) return value.length > 0;
      return true;
    }),
  );

/**
 * The alias the record uuid travels under — `RECORD_KEY_ALIAS` in the payload
 * builder, repeated here rather than imported: a service reaching into
 * `domains/` would invert the dependency.
 */
const RECORD_KEY_ALIAS = 'record_uuid';

/**
 * The columns the author actually picked.
 *
 * The rev4 backend owns the record uuid column: it adds one to the dataset it
 * generates and a widget read back no longer lists it (dev, 2026-10-20). A
 * widget saved before that still carries the hidden column this builder used
 * to append, and re-offering it as an ordinary column would make the next
 * save send it twice — so it is dropped by name (CT-5641).
 */
const exposedRecordSetColumns = (columns) =>
  columns.filter((column) => column?.alias !== RECORD_KEY_ALIAS);

/**
 * Which exposed column, or columns, each binding slot points at.
 *
 * The contract binds query aliases; the builder stores field uuids. Slots
 * bound to something that is not an exposed column — the record key, which is
 * assigned rather than asked — are left out: the Configure step only asks
 * about columns, so it must only be handed answers it could have given.
 *
 * A **list slot** (`columns`, `identity`, `card_fields`…) comes back as the
 * list the author picked, in its stored order. Reading only the single-object
 * shape dropped those answers on reopen, and the next save let the automatic
 * assignment refill them with whatever came first.
 */
const mapBindingsToSlotAssignments = (bindings, columns) => {
  if (!bindings || typeof bindings !== 'object') return {};
  const fieldUuidByAlias = Object.fromEntries(
    exposedRecordSetColumns(columns).map((column) => [column.alias, column.fieldUuid ?? '']),
  );
  /*
    `field` is the source column since rev4, and `field_key` is the output
    key derived from it — `total_amount_sum`, `col_2_2` — which is not an
    alias of the record set and would resolve to nothing. A widget saved
    before the change carries only `field_key`, and there it *is* the
    alias, so both are tried in that order (CT-5641).
  */
  const uuidOf = (item) => fieldUuidByAlias[item?.field] ?? fieldUuidByAlias[item?.field_key];

  return Object.entries(bindings).reduce((acc, [slot, binding]) => {
    if (Array.isArray(binding)) {
      const fieldUuids = binding.map(uuidOf).filter(Boolean);
      return fieldUuids.length ? { ...acc, [slot]: fieldUuids } : acc;
    }
    const fieldUuid = uuidOf(binding);
    return fieldUuid ? { ...acc, [slot]: fieldUuid } : acc;
  }, {});
};

/**
 * Builder configuration, rebuilt from the definition the API stores.
 *
 * `visualizationConfig` is the contract's free-form presentation bag and the
 * only place the builder's own state (operation, groupers, exposed columns,
 * preview size) can live — the definition has no fields of its own for them.
 */
const mapDataSourceToBuilderConfig = (raw = {}) => {
  const dataSource = raw.dataSource ?? {};
  const entity = dataSource.entity ?? {};
  const visualization = raw.visualizationConfig ?? {};
  const recordSetColumns = entity.recordSet?.columns ?? [];

  // A widget with neither a data source nor a stored builder bag was not made
  // in the Playground; `null` is what `useWidgetBuilderForm` reads as "start
  // from defaults", and an empty object there would present an empty Data step
  // as if it had been configured.
  if (!dataSource.mode && !dataSource.queryUuid && !Object.keys(visualization).length) {
    return null;
  }

  const datasetId = dataSource.mode === 'EXISTING_QUERY' ? (dataSource.queryUuid ?? '') : '';
  const sourceType = visualization.sourceType
    ?? (dataSource.mode === 'EXISTING_QUERY' ? 'dataset' : 'entity');

  return {
    sourceType,
    /*
      The composite the widget reads from, and the member entity the picker
      showed — the member is what `field/list` takes, so it has to survive the
      round trip or reopening for edit would have no columns to offer.

      A widget built on a dataset the author picked has no member to name:
      `requestDataset` clears it, because the dataset *is* the source. Its
      source id is the dataset's, which is what `useSourceColumns` asks for
      (CT-5860) — without this the Data step reopened saying "Configure a
      dataset on the Data tab to begin" over a widget that had one.
    */
    sourceId: sourceType === 'dataset'
      ? datasetId
      : (visualization.memberEntityUuid ?? entity.compositeEntityUuid ?? ''),
    compositeEntityUuid: entity.compositeEntityUuid ?? '',
    entityRef: visualization.memberEntityUuid ?? '',
    sourceLabel: raw.sourceLabel ?? visualization.sourceLabel ?? '',
    sourceIntegration: visualization.integration ?? '',
    // Only when the widget really is dataset-backed. An ENTITY widget also has
    // a `queryUuid` — the query the backend generated for it — and reading
    // that as a chosen dataset made the next save send it as `datasetId`, and
    // would make an unlabelled widget hydrate as `sourceType: 'dataset'`.
    datasetId,
    datasetShape: visualization.datasetShape ?? '',
    operationType: RESULT_MODE_TO_OPERATION[entity.resultMode] ?? '',
    filters: (entity.filters ?? []).map((filter) => ({
      fieldUuid: filter.fieldUuid ?? '',
      column: filter.fieldUuid ?? '',
      // Back into the builder's vocabulary. An operator the map has never
      // seen is left as it came rather than blanked: the row then shows
      // something wrong, which is findable, instead of showing nothing.
      operator: FILTER_OPERATOR_FROM_WIRE[filter.operator] ?? filter.operator ?? '',
      // `literal` is where the contract keeps it — see `buildWidgetPayload`.
      value: filter.value?.literal ?? '',
      valueTo: '',
    })),
    /*
      The measures, back from the aggregations they were sent as. Without
      this the Data step reopens with an empty Measures section and the next
      save silently turns an average back into a row count — which the rev4
      backend then refuses, because that is a different dataset (CT-5641).

      A row count is hydrated as an explicit row rather than as the empty
      default: it reads the same on the wire and it shows the author what the
      widget actually does.
    */
    measures: (entity.summary?.aggregations ?? []).map((aggregation) => ({
      fieldUuid: aggregation.fieldUuid ?? '',
      column: aggregation.fieldUuid ?? '',
      aggregate: aggregation.function ?? '',
      // The alias the aggregation travels under is what the bindings point
      // at, so it is the identity that matches a saved slot to this row —
      // two `SUM` rows are told apart by `sum_amount` vs `sum_quota` and by
      // nothing else (`restoreSlotAssignments`).
      alias: aggregation.alias ?? '',
    })),
    groupers: (entity.summary?.groupers ?? []).map((grouper) => ({
      fieldUuid: grouper.fieldUuid ?? '',
      column: grouper.fieldUuid ?? '',
      alias: grouper.alias ?? '',
    })),
    exposedColumns: recordSetColumns.length
      ? exposedRecordSetColumns(recordSetColumns).map((column) => column.fieldUuid ?? '')
      : null,
  };
};

/**
 * WidgetDefinition → the shape the library row, the detail panel and the
 * builder all read.
 */
export const mapApiWidgetToUi = (raw = {}) => ({
  builderConfig: mapDataSourceToBuilderConfig(raw),
  // Which column the author said is the title, the lane, the severity. Read
  // back off the stored contract rather than kept in a bag of its own: the
  // bindings **are** the answer, and a second copy could disagree with them.
  slotAssignments: mapBindingsToSlotAssignments(
    raw.visualizationConfig?.bindings,
    raw.dataSource?.entity?.recordSet?.columns ?? [],
  ),
  // The contract as stored. The builder asks about measure and dimension
  // slots too since fix/widgets, and those are answered against form rows
  // this service knows nothing about — see `restoreSlotAssignments`.
  bindings: raw.visualizationConfig?.bindings ?? null,
  // The type's look-and-feel options, as stored. Passed through rather than
  // flattened here: the shape the form holds is the builder's business, and a
  // service reaching into `domains/` would invert the dependency (CT-5579).
  display: raw.visualizationConfig?.display ?? null,
  endUserFiltersEnabled: raw.userFilters?.enabled ?? false,
  id: raw.widgetUuid ?? '',
  widgetCode: raw.widgetCode ?? '',
  // Opaque concurrency token: stored as received and echoed back on the next
  // write. It is a timestamp today; nothing here may depend on that.
  versionStamp: raw.versionStamp ?? null,
  queryUuid: raw.dataSource?.queryUuid ?? null,
  name: raw.name ?? '',
  description: raw.description ?? '',
  type: raw.typeCode ?? '',
  // `sourceLabel` is the display string the row prints. It is a string in the
  // contract, and the row renders it directly, so anything else is coerced
  // away rather than allowed to reach React as an object.
  source: typeof raw.sourceLabel === 'string' ? raw.sourceLabel : '',
  category: raw.categoryKey ?? '',
  governed: raw.isGoverned ?? false,
  // Lowercased because the row keys its badge (and its i18n) off this value;
  // the wire vocabulary is `DRAFT` / `PUBLISHED`.
  status: (raw.status ?? 'DRAFT').toLowerCase(),
  origin: raw.origin ?? '',
  provenance: raw.provenance ?? '',
  isPublic: raw.isPublic ?? false,
  versionNumber: raw.versionNumber ?? null,
  refreshSeconds: raw.refreshSeconds ?? null,
  size: raw.visualizationConfig?.size ?? 'medium',
  // No counterpart in the WidgetDefinition contract (verified against the live
  // stage). Left empty rather than invented — the row and the panel already
  // hide these when they are falsy.
  integration: '',
  profile: '',
  freshness: '',
  // `null`, not `0`: the row and the panel read null as "unknown" and hide the
  // placement line entirely, which is the truth — a count of 0 would claim the
  // widget is on no dashboard.
  usedOnDashboards: null,
  dashboards: [],
  lastUpdated: null,
});

/**
 * List row → the card the library renders.
 *
 * A different shape from the definition the `GET` returns — it carries
 * `usedOnDashboards` and `hasDraft`, and carries no data source — so it gets
 * its own mapper rather than being forced through the one above.
 */
export const mapApiWidgetRowToUi = (raw = {}) => ({
  id: raw.widgetUuid ?? '',
  widgetCode: raw.widgetCode ?? '',
  name: raw.name ?? '',
  description: raw.description ?? '',
  type: raw.typeCode ?? '',
  category: raw.categoryKey ?? '',
  source: typeof raw.sourceLabel === 'string' ? raw.sourceLabel : '',
  origin: raw.origin ?? '',
  provenance: raw.provenance ?? '',
  implementationType: raw.implementationType ?? '',
  governed: raw.isGoverned ?? false,
  isPublic: raw.isPublic ?? false,
  status: (raw.status ?? 'DRAFT').toLowerCase(),
  publishedVersionNumber: raw.publishedVersionNumber ?? null,
  hasDraft: raw.hasDraft ?? false,
  // An object on the wire, split by where the placement lives. The row prints
  // one number, and a draft placement is still a placement.
  usedOnDashboards:
    (raw.usedOnDashboards?.published ?? 0) + (raw.usedOnDashboards?.draftOnly ?? 0),
  usedOnPublishedDashboards: raw.usedOnDashboards?.published ?? 0,
  // Not on the row: the library shows neither, and the detail panel reads the
  // definition instead.
  builderConfig: null,
  endUserFiltersEnabled: false,
  dashboards: [],
  integration: '',
  profile: '',
  freshness: '',
  size: 'medium',
  lastUpdated: null,
});

/**
 * Create / update / publish return an operation result, not a definition: the
 * uuid plus the concurrency and validation state the builder has to act on.
 */
export const mapApiWidgetWriteResult = (raw = {}) => ({
  id: raw.widgetUuid ?? '',
  widgetCode: raw.widgetCode ?? '',
  origin: raw.origin ?? '',
  status: (raw.status ?? 'DRAFT').toLowerCase(),
  draftVersionNumber: raw.draftVersionNumber ?? null,
  publishedVersionNumber: raw.publishedVersionNumber ?? null,
  publishedAt: raw.publishedAt ?? null,
  contractHash: raw.contractHash ?? null,
  // What the next write has to echo as `expectedVersionStamp`; without it two
  // tabs silently overwrite each other instead of getting DSW011.
  versionStamp: raw.versionStamp ?? null,
  revision: raw.revision ?? null,
  queryUuid: raw.queryUuid ?? null,
  validationStatus: raw.validationStatus ?? null,
  validationIssues: raw.validationIssues ?? [],
});

/**
 * List widgets — searched, filtered, sorted and paged server-side.
 *
 * `governedCount` needs a total, not a per-page tally, and the contract has no
 * facet counts, so it comes from a parallel count request under the same
 * filters. One extra call per query, and it is the only way the header's
 * "N widgets · M governed" is true on page 2. Callers that do not show the
 * stat pass `withGovernedCount: false` and skip it.
 *
 * `searchKeyword` is a plain string on the wire — `ListWidgetsRequest` has no
 * `search: { query, fields }` object field, which Pydantic silently drops.
 */
export async function listWidgets({
  page,
  pageSize,
  orderBy,
  orderDirection,
  filters = {},
  searchKeyword,
  withGovernedCount = true,
} = {}) {
  const payload = compact({
    page: page ?? 1,
    pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
    orderBy: orderBy ?? 'name',
    // The API validates this one: it must be `ASC` or `DESC`.
    orderDirection: (orderDirection ?? 'asc').toUpperCase(),
    categoryKeys: filters.categoryKeys,
    typeCodes: filters.typeCodes,
    sectionKeys: filters.sectionKeys,
    status: filters.status,
    origin: filters.origin,
    provenance: filters.provenance,
    isGoverned: filters.isGoverned,
    searchKeyword,
  });

  // When the user has filtered *by* governance the answer is already known —
  // every row matches or none does — so the count is derived rather than
  // asked for. Overriding `isGoverned` in a second request would otherwise
  // count governed widgets the list is deliberately hiding, and the header
  // would claim more of them than the page shows.
  // Skipped when the caller does not render the stat — the builder's widget
  // picker walks every page, and a second request per page for a number
  // nothing displays is pure waste.
  const isGovernanceFiltered = typeof filters.isGoverned === 'boolean' || !withGovernedCount;

  const [response, governedResponse] = await Promise.all([
    apiDashboards.post(`${WIDGET_API_PREFIX}/list`, payload),
    isGovernanceFiltered
      ? null
      : apiDashboards.post(`${WIDGET_API_PREFIX}/list`, {
        ...payload,
        page: 1,
        pageSize: 1,
        isGoverned: true,
      }),
  ]);

  const { body, meta } = validateApiResponse(response, 'Could not load widgets.');
  const normalizedMeta = normalizePagedMeta({
    perPage: payload.pageSize,
    currentPage: payload.page,
    ...(meta ?? {}),
  });

  // A count request that fails must not take the list down with it; the header
  // stat degrades to 0 and the widgets still render.
  let governedCount = 0;
  try {
    if (isGovernanceFiltered) {
      governedCount = withGovernedCount && filters.isGoverned
        ? (normalizedMeta?.totalItems ?? 0)
        : 0;
    } else {
      const governed = validateApiResponse(governedResponse);
      governedCount = normalizePagedMeta(governed.meta)?.totalItems ?? 0;
    }
  } catch {
    governedCount = 0;
  }

  return {
    widgets: (body ?? []).map(mapApiWidgetRowToUi),
    meta: normalizedMeta,
    total: normalizedMeta?.totalItems ?? 0,
    governedCount,
  };
}

/**
 * Read one widget definition.
 *
 * @param {string} widgetId `widgetUuid`.
 * @param {{ version?: 'PUBLISHED' | 'DRAFT' }} [options] Which stored version to
 *   read. The builder edits the draft; the library previews what is published.
 */
export async function getWidget(widgetId, { version = 'PUBLISHED' } = {}) {
  const response = await apiDashboards.get(
    `${WIDGET_API_PREFIX}/${encodeURIComponent(widgetId)}`,
    { params: { version } },
  );
  const { body } = validateApiResponse(response, 'Could not load widget.');
  return mapApiWidgetToUi(body ?? {});
}

/**
 * Create a widget. It is born a `DRAFT` — `publishWidget` is what puts it in
 * the catalog.
 */
export async function createWidget(payload = {}) {
  const response = await apiDashboards.post(`${WIDGET_API_PREFIX}/create`, payload);
  const { body } = validateApiResponse(response, 'Could not save the widget.');
  return mapApiWidgetWriteResult(body ?? {});
}

/**
 * Update a widget's metadata and draft definition in one call.
 *
 * `expectedVersionStamp` is the optimistic-concurrency token: pass the stamp
 * the last read or write returned and a stale tab gets `DSW011` instead of
 * silently overwriting someone else's edit. It is opaque — store and return it,
 * never parse it.
 */
export async function updateWidget(widgetId, payload = {}, { expectedVersionStamp } = {}) {
  const response = await apiDashboards.post(`${WIDGET_API_PREFIX}/update`, {
    ...payload,
    widgetUuid: widgetId,
    ...(expectedVersionStamp ? { expectedVersionStamp } : {}),
  });
  const { body } = validateApiResponse(response, 'Could not save the widget.');
  return mapApiWidgetWriteResult(body ?? {});
}

/**
 * Publish the draft. A failed validation comes back as `DSW012` with a
 * validation report, which `validateApiResponse` turns into a throw carrying
 * the backend's own message.
 */
export async function publishWidget(widgetId, { expectedVersionStamp, versionNotes } = {}) {
  const response = await apiDashboards.post(`${WIDGET_API_PREFIX}/publish`, {
    widgetUuid: widgetId,
    ...(expectedVersionStamp ? { expectedVersionStamp } : {}),
    ...(versionNotes ? { versionNotes } : {}),
  });
  const { body } = validateApiResponse(response, 'Could not publish the widget.');
  return mapApiWidgetWriteResult(body ?? {});
}

/**
 * Soft-delete a widget. Blocked by the backend with `DSW013` while the widget
 * is placed on a dashboard.
 *
 * The endpoint answers `200 { deleted: false }` when nothing was removed, so
 * the flag — not the status code — is what decides success here; otherwise the
 * library would show a delete toast for a row that is still there.
 */
export async function deleteWidget(widgetId, { expectedVersionStamp } = {}) {
  const response = await apiDashboards.post(`${WIDGET_API_PREFIX}/delete`, {
    widgetUuid: widgetId,
    ...(expectedVersionStamp ? { expectedVersionStamp } : {}),
  });
  const { body } = validateApiResponse(response, 'Could not delete the widget.');
  if (body?.deleted !== true) {
    throw new Error('Could not delete the widget.');
  }
  return true;
}
